import { Grid } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { giftconditionApis } from 'api'
import { NA } from 'constans/common'
import { GIFT_CONDITIONS } from 'constans/queryKeys'
import * as urls from 'constans/urls'
import useToastify from 'hooks/useToastify'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, StatusCard, Text } from 'shared/components'
import DateTimeCell from 'shared/components/DateTimeCell'
import {
  GiftConditionDeleteModel,
  GiftConditionModel,
} from 'types/gift-conditions'

const useGiftConditionColumns = () => {
  const [currentId, setCurrentId] = useState<number | undefined>()
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastify()
  const queryClient = useQueryClient()

  const { mutate, isPending } = useMutation({
    mutationFn: (val: GiftConditionDeleteModel) =>
      giftconditionApis.delete(val),
    onSuccess: d => {
      if (d?.ErrorCode === 0) {
        showSuccess('Xóa điều kiện thành công')
        queryClient.invalidateQueries({
          queryKey: [GIFT_CONDITIONS],
        })
      } else {
        showError('Đã xảy ra lỗi, vui lòng thử lại!')
      }
    },
    onError: () => {
      showError('Đã xảy ra lỗi, vui lòng thử lại!')
    },
  })

  const columnHelper = createColumnHelper<GiftConditionModel>()

  const columns = useMemo(
    () => [
      columnHelper.accessor('CreatedDate', {
        enableResizing: false,
        size: 200,
        header: 'Ngày tạo',
        cell: val => <DateTimeCell width={200} value={val.getValue()} />,
      }),
      columnHelper.accessor('ProductName', {
        enableResizing: false,
        minSize: 200,
        header: 'Tên sản phẩm',
        cell: val => <Text minWidth={300}>{val.getValue() || NA}</Text>,
      }),
      columnHelper.accessor('ProductCode', {
        header: 'Mã sản phẩm',
        cell: val => <Text minWidth={120}>{val.getValue() || NA}</Text>,
      }),
      columnHelper.accessor('TemQuantity', {
        header: 'Số lượng tem',
        cell: val => <Text minWidth={120}>{val.getValue() || NA}</Text>,
      }),
      columnHelper.accessor('IsActive', {
        header: 'Trạng thái',
        size: 180,
        cell: ({ row: { original } }) => {
          return (
            <StatusCard
              disableBgColor
              width={150}
              label={
                !original?.IsDeleted && original?.IsActive
                  ? 'Hoạt động'
                  : 'Không hoạt động'
              }
              type={
                !original?.IsDeleted && original?.IsActive ? 'active' : 'error'
              }
            />
          )
        },
      }),

      columnHelper.display({
        header: 'Hành động',
        cell: val => {
          const { Id, UpdatedUser, CreatedUser, IsDeleted } = val.row.original
          if (IsDeleted === 1) return <>{NA}</>
          return (
            <Grid gap={1} container flexWrap="nowrap">
              <Button
                disabled={isPending && currentId === Id}
                onClick={() =>
                  navigate(
                    `/${urls.GIFT_CONDITION_URLS.DETAILS.replace(
                      ':conditionId',
                      `${val.row.original.Id}`
                    )}`
                  )
                }
                color="info"
              >
                Chi tiết
              </Button>
              <Button
                disabled={isPending && currentId === Id}
                onClick={() =>
                  navigate(
                    `/${urls.GIFT_CONDITION_URLS.EDIT.replace(
                      ':conditionId',
                      `${val.row.original.Id}`
                    )}`
                  )
                }
                color="warning"
              >
                Cập nhật
              </Button>
              <Button
                loading={isPending && currentId === Id}
                onClick={() => {
                  setCurrentId(Id)
                  mutate({
                    id: Id,
                    deletedUser: UpdatedUser || CreatedUser || 'Admin',
                  })
                }}
                color="error"
              >
                Xóa
              </Button>
            </Grid>
          )
        },
      }),
    ],
    [currentId, isPending, mutate, columnHelper, navigate]
  )
  return columns as Array<ColumnDef<GiftConditionModel, string>>
}

export default useGiftConditionColumns
